"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import { m, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRouter } from "next/navigation";

import TimelineFeatures from "./TimelineFeatures";
import TimelineImportantMilestones from "./TimelineImportantMilestones";
import TimelineClientHandover from "./TimelineClientHandover";
import TimelineAll from "./TimelineAll";
import TimelineModal from "./TimelineModal";  
import { CoreCommitment as Icon_CoreCommitment } from "@/components/customer/icons/CoreCommitment";
import { CoreGrowth as Icon_CoreGrowth } from "@/components/customer/icons/CoreGrowth";
import { CoreOwnership as Icon_CoreOwnership } from "@/components/customer/icons/CoreOwnership";
import { CorePositivity as Icon_CorePositivity } from "@/components/customer/icons/CorePositivity";
import { CoreTeamwork as Icon_CoreTeamwork } from "@/components/customer/icons/CoreTeamwork";
import { CoreValueInnovation as Icon_CoreValueInnovation } from "@/components/customer/icons/CoreValueInnovation";
const SvgIcons = { CoreCommitment: Icon_CoreCommitment, CoreGrowth: Icon_CoreGrowth, CoreOwnership: Icon_CoreOwnership, CorePositivity: Icon_CorePositivity, CoreTeamwork: Icon_CoreTeamwork, CoreValueInnovation: Icon_CoreValueInnovation };

import type { TimelineEvent } from "./types";
import { getApiUrl } from "@/lib/apiUrl";

const tabs = ["All", "Important Milestones", "Client Handover", "Features"] as const;
type Tab = (typeof tabs)[number];

const sortYearsDesc = (a: string, b: string) => Number(b) - Number(a);

const deriveYears = (items: TimelineEvent[]) =>
    Array.from(new Set(items.map((x) => x.year).filter((y): y is string => Boolean(y)))).sort(sortYearsDesc);

export default function VilvaTimeLineView({ initialTimelines = [] }: { initialTimelines?: TimelineEvent[] }) {
    const router = useRouter();
    const [selectedTimeline, setSelectedTimeline] = useState<TimelineEvent | null>(null);  // ✅ typed
    const [activeTab, setActiveTab] = useState<Tab>("All");
    // Seed from server-rendered data so the milestones are in the SSR HTML (crawlable) instead
    // of only appearing after a client fetch. State derivations mirror the fetch effect exactly.
    const [timelineData, setTimelineData] = useState<TimelineEvent[]>(initialTimelines);
    const [years, setYears] = useState<string[]>(() => deriveYears(initialTimelines));
    const [activeYear, setActiveYear] = useState<string>(() => deriveYears(initialTimelines)[0] || "");
    const [currentYearIndex, setCurrentYearIndex] = useState(0);

    const yearsPerPage = 4;

    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ target: containerRef });
    const lineHeight = useTransform(scrollYProgress, [0.1, 0.9], ["10%", "90%"]);

    useEffect(() => {
        // The server already provided milestones via ISR (revalidate: 60); skip the client
        // refetch to avoid a double fetch + content flash. Only fetch here if the build-time
        // request returned nothing (e.g. API was unreachable at build).
        if (initialTimelines.length > 0) return;
        const fetchTimelineData = async () => {
            try {
                const res = await fetch(getApiUrl("/v1/timelines"), {
                    cache: "no-store",
                });
                const result = await res.json();

                if (!result?.status || !Array.isArray(result?.data)) return;

                const mapped: TimelineEvent[] = result.data.map((item: any) => {
                    return {
                        id: Number(item.id),
                        timeline_date: String(item.timeline_date ?? ""),
                        year: String(item.year ?? ""),
                        month: item.month ? Number(item.month) : null,   // ✅ add this
                        day: item.day ? Number(item.day) : null,         // ✅ add this
                        title: item.title ?? "",
                        description: item.description ?? "",
                        image: item.image_1 ?? "",
                        is_featured: Number(item.is_featured ?? 0),
                        category_name: item.category_name ?? null,
                    } satisfies TimelineEvent;
                });

                setTimelineData(mapped);

                const uniqueYears: string[] = Array.from(
                    new Set(mapped.map((x) => x.year).filter((y): y is string => Boolean(y)))
                ).sort(sortYearsDesc);

                setYears(uniqueYears);
                setActiveYear((prev) => prev || uniqueYears[0] || "");
                setCurrentYearIndex(0);
            } catch (err) {
                console.error("Error fetching timeline data:", err);
            }
        };

        fetchTimelineData();
    }, []);

    // Read the ?tab= param client-side (in an effect) instead of useSearchParams(),
    // which would force this page to bail to client-side rendering and empty its
    // crawlable HTML.
    useEffect(() => {
        const tabFromUrl = (new URLSearchParams(window.location.search).get("tab") || "") as Tab;
        if (tabFromUrl && tabs.includes(tabFromUrl)) setActiveTab(tabFromUrl);
    }, []);

    const handleTabClick = (tab: Tab) => {
        setActiveTab(tab);
        router.push(`?tab=${encodeURIComponent(tab)}`, { scroll: false });
    };

    const handlePrevious = () => {
        if (currentYearIndex > 0) setCurrentYearIndex((p) => p - 1);
    };

    const handleNext = () => {
        if (currentYearIndex < Math.max(0, years.length - yearsPerPage)) {
            setCurrentYearIndex((p) => p + 1);
        }
    };

    const visibleYears = years.slice(currentYearIndex, currentYearIndex + yearsPerPage);

    const yearFilteredData = useMemo(() => {
        if (!activeYear) return timelineData;
        return timelineData.filter((x) => x.year === activeYear);
    }, [timelineData, activeYear]);

    const tabFilteredData = useMemo(() => {
        switch (activeTab) {
            case "All":
                return yearFilteredData;
            case "Important Milestones":
                return yearFilteredData.filter((x) => x.is_featured === 1);
            case "Client Handover":
                return yearFilteredData.filter(
                    (x) =>
                        x.title.toLowerCase().includes("handover") ||
                        (x.category_name ?? "").toLowerCase().includes("handover")
                );
            case "Features":
                return yearFilteredData.filter(
                    (x) =>
                        x.title.toLowerCase().includes("launch") ||
                        x.title.toLowerCase().includes("feature") ||
                        x.title.toLowerCase().includes("product") ||
                        x.title.toLowerCase().includes("portal") ||
                        (x.category_name ?? "").toLowerCase().includes("feature")
                );
            default:
                return yearFilteredData;
        }
    }, [yearFilteredData, activeTab]);

    // ✅ Only open modal if is_featured === 1
    const handleTimelineClick = (item: TimelineEvent) => {
        if (item.is_featured === 1) {
            setSelectedTimeline(item);
        }
    };

    const renderTimeline = () => {
        switch (activeTab) {
            case "All":
                return <TimelineAll data={tabFilteredData} onItemClick={handleTimelineClick} />;
            case "Important Milestones":
                return <TimelineImportantMilestones data={tabFilteredData} onItemClick={handleTimelineClick} />;
            case "Client Handover":
                return <TimelineClientHandover data={tabFilteredData} onItemClick={handleTimelineClick} />;
            case "Features":
                return <TimelineFeatures data={tabFilteredData} onItemClick={handleTimelineClick} />;
            default:
                return null;
        }
    };

    return (
        <div ref={containerRef} className="bg-[#EFF1F5] py-10 pb-0 overflow-x-hidden">
            {/* ✅ Modal — renders above everything when selectedTimeline is set */}
            <TimelineModal
                item={selectedTimeline}
                onClose={() => setSelectedTimeline(null)}
            />

            {/* Header */}
            <m.h1
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-[16px] text-[var(--Dark-Color,#090D28)] text-center font-NeueMontreal text-[36px] md:text-[66px] font-normal leading-[120%]"
            >
                Vilva's Journey of <br />{" "}
                <span className="!text-[#008FEE] font-NeueMontreal text-[36px] md:text-[66px] font-normal leading-[120%]">
                    Growth & Innovation
                </span>
            </m.h1>

            <m.p
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-[#373844] m-[0px] mb-[60px] w-full mx-auto md:w-[40%] text-center font-Schibsted_Grotesk text-[20px] font-normal leading-[150%]"
            >
                Every step we've taken has been guided by real needs, real feedback, and real people — see how we got here.
            </m.p>

            {/* Stats */}
            <m.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="grid w-full md:w-[45%] mx-auto mb-[80px] grid-cols-2 md:grid-cols-3 md:gap-[24px] gap-[8px] md:px-0 px-2"
            >
                <div>
                    <div className="flex p-6 flex-col items-start rounded-[12px] bg-[#D6ECFB]">
                        <p className="text-[var(--Dark-Color,#090D28)] font-NeueMontreal text-[36px] font-normal m-0">10+</p>
                        <p className="text-[#6A8A9F] font-Schibsted_Grotesk text-base font-normal m-0">Years in business</p>
                    </div>
                </div>
                <div>
                    <div className="flex p-6 flex-col items-start rounded-[12px] bg-[#D6ECFB]">
                        <p className="text-[var(--Dark-Color,#090D28)] font-NeueMontreal text-[36px] font-normal m-0">1000+</p>
                        <p className="text-[#6A8A9F] font-Schibsted_Grotesk text-base font-normal m-0">Happy Customers</p>
                    </div>
                </div>
                <div>
                    <div className="flex p-6 flex-col items-start rounded-[12px] bg-[#D6ECFB]">
                        <p className="text-[var(--Dark-Color,#090D28)] font-NeueMontreal text-[36px] font-normal m-0">50+</p>
                        <p className="text-[#6A8A9F] font-Schibsted_Grotesk text-base font-normal m-0">Projects Delivered</p>
                    </div>
                </div>
            </m.div>

            {/* Tabs */}
            <div className="max-w-4xl mx-auto mt-10 md:mt-12 md:px-0 px-2">
                <div className="flex w-full items-center justify-start md:justify-center gap-8 md:gap-16 bg-white rounded-[12px] shadow-sm px-4 md:px-12 border border-gray-100 overflow-x-auto whitespace-nowrap scrollbar-hide relative">
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => handleTabClick(tab)}
                            className={`relative px-6 py-4 font-medium text-lg whitespace-nowrap transition-colors z-10 font-Schibsted_Grotesk
                                ${activeTab === tab ? "text-[#008FEE]" : "text-[#6B768A] hover:text-[#008FEE]"}`}
                        >
                            {tab}
                            {activeTab === tab && (
                                <m.span
                                    layoutId="activeTabUnderline"
                                    className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#008FEE] rounded-full"
                                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Year Navigation */}
            <div className="top-24 z-10 mt-12">
                <div className="max-w-6xl mx-auto px-4">
                    <m.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="flex items-center justify-center gap-4 md:gap-[24px] backdrop-blur-md w-full md:w-fit mx-auto px-3 md:px-0 py-[8px] overflow-x-auto whitespace-nowrap scrollbar-hide"
                    >
                        <m.button
                            onClick={handlePrevious}
                            aria-label="Show earlier years"
                            className="rounded-full transition-all duration-300 text-[#090D28]"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            disabled={currentYearIndex === 0}
                        >
                            <ChevronLeft className="w-5 h-5 text-slate-400" />
                        </m.button>

                        {visibleYears.map((year) => (
                            <m.button
                                key={year}
                                onClick={() => setActiveYear(year)}
                                className={`rounded-full font-semibold transition-all duration-300 text-center font-Schibsted_Grotesk text-[18px] font-normal
                                    ${activeYear === year ? "text-[#008FEE]" : "text-[#090D28]"}`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {year}
                            </m.button>
                        ))}

                        <m.button
                            onClick={handleNext}
                            aria-label="Show later years"
                            className="rounded-full transition-all duration-300 text-[#090D28]"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            disabled={currentYearIndex + yearsPerPage >= years.length}
                        >
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                        </m.button>
                    </m.div>
                </div>
            </div>

            {/* Empty state */}
            {tabFilteredData.length === 0 && (
                <div className="text-center py-16 text-[#6B768A] font-Schibsted_Grotesk text-lg">
                    No timeline entries found for this filter.
                </div>
            )}

            {/* Timeline */}
            <div className="max-w-5xl mx-auto px-4 relative pb-32 md:pb-48">
                <div className="absolute top-0 bottom-0 left-4 md:left-1/2 w-0.5 bg-gray-200 md:-translate-x-1/2" />
                <m.div
                    className="absolute top-0 left-4 md:left-1/2 w-0.5 bg-[#008FEE] origin-top md:-translate-x-1/2"
                    style={{ height: lineHeight }}
                />
                {renderTimeline()}
            </div>

            {/* Core Values */}
            <section className="bg-[#FFFFFF]">
                <section className="px-4 md:container py-[80px] md:py-[120px]">
                    <h2 className="text-[#090D28] text-center font-NeueMontreal text-[32px] mb-[48px] md:text-[48px] font-normal leading-[120%]">
                        The Core Values That Drive Vilva
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[24px]">
                        <div className="core-value-card rounded-3xl bg-[#EFFCE9] p-6">
                            <SvgIcons.CoreValueInnovation />
                            <p className="text-[#5BBE2A] py-[16px] text-[22px] leading-[120%] font-[700] font-Schibsted_Grotesk">Innovation</p>
                            <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                We embrace emerging technologies and creative thinking to deliver future-ready products that give our clients a competitive edge.
                            </p>
                        </div>
                        <div className="core-value-card rounded-3xl bg-[#E9F5FC] p-6">
                            <SvgIcons.CoreGrowth />
                            <p className="text-[#157FE3] py-[16px] text-[22px] leading-[120%] font-[700] font-Schibsted_Grotesk">Growth</p>
                            <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                We focus on building scalable and efficient solutions that fuel real business growth and long-term success.
                            </p>
                        </div>
                        <div className="core-value-card rounded-3xl bg-[#FCF9E9] p-6">
                            <SvgIcons.CoreOwnership />
                            <p className="text-[#B67D19] py-[16px] text-[22px] leading-[120%] font-[700] font-Schibsted_Grotesk">Ownership</p>
                            <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                From start to finish, we own every detail ensuring accountability, quality, and on-time delivery.
                            </p>
                        </div>
                        <div className="core-value-card rounded-3xl bg-[#FDF2F7] p-6">
                            <SvgIcons.CoreTeamwork />
                            <p className="text-[#E52F69] py-[16px] text-[20px] leading-[120%] font-[700] font-Schibsted_Grotesk">Teamwork</p>
                            <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                We work hand-in-hand across roles and ideas, building synergy that leads to smarter, smoother execution.
                            </p>
                        </div>
                        <div className="core-value-card rounded-3xl bg-[#E9FCF6] p-6">
                            <SvgIcons.CoreCommitment />
                            <p className="text-[#1CAF80] py-[16px] text-[22px] leading-[120%] font-[700] font-Schibsted_Grotesk">Commitment</p>
                            <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                Our word is our bond. We prioritize reliability and consistency in every phase of the project.
                            </p>
                        </div>
                        <div className="core-value-card rounded-3xl bg-[#F3EDFC] p-6">
                            <SvgIcons.CorePositivity />
                            <p className="text-[#8245DC] py-[16px] text-[22px] leading-[120%] font-[700] font-Schibsted_Grotesk">Positivity</p>
                            <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                We face challenges with optimism, fostering a supportive, high-energy environment for both team and clients.
                            </p>
                        </div>
                    </div>
                </section>
            </section>
        </div>
    );
}